import { MembershipTier, BillingPeriod } from '../../models/membership.model';
import { TIER_COLOR_MAP } from '../../constants/membership.constants';
import { GreenCheck, GreyDash } from './MembershipIcons';

export function CompareTable({ tiers, billing, onBecomeMember, checkoutLoading }: { tiers: MembershipTier[]; billing: BillingPeriod; onBecomeMember?: (membershipId?: string) => void; checkoutLoading?: boolean }) {
   const allTiers = tiers; // White, Silver, Gold, Diamond
   const compareColumns = allTiers.map((t) => {
      const isAnnual = billing === 'annual' && !t.monthlyOnly;
      const displayPrice = isAnnual ? t.price.annualPerMonth : t.price.monthly;
      const displayMultiplier = isAnnual ? t.multiplier.annual : t.multiplier.monthly;

      return {
         id: t.id,
         label: t.type.charAt(0) + t.type.slice(1).toLowerCase(),
         slug: t.slug,
         color: TIER_COLOR_MAP[t.type] ?? '#FFFFFF',
         price: `$${displayPrice.toFixed(2)}`,
         multiplier: String(displayMultiplier),
         entries: t.sweepstakesEntries.toLocaleString(),
         spins: String(t.pylSpins),
         dge: t.dgeIncluded ? ('included' as const) : ('optional' as const),
         badge: t.type !== 'WHITE',
         chat: true,
         coordinator: true,
         founder: t.type === 'DIAMOND',
         popular: t.popular,
         action: t.action,
      };
   });

   return (
      <div className="compare-section">
         <p className="compare-eyebrow">Compare Memberships</p>
         <h2 className="compare-heading">Find the plan that fits your game.</h2>

         <div className="compare-table">
            <div className="compare-table-inner">
            {/* Silver highlight column */}
            <div className="compare-silver-highlight" />

            {/* Vertical column dividers */}
            <div className="compare-vdivider" style={{ left: '31.07%' }} />
            <div className="compare-vdivider" style={{ left: '48.30%' }} />
            <div className="compare-vdivider" style={{ left: '65.54%' }} />
            <div className="compare-vdivider" style={{ left: '82.77%' }} />

            {/* Column headers */}
            <div className="compare-headers">
               <div className="compare-label-col" />
               {compareColumns.map((col) => (
                  <div key={col.slug} className="compare-col-header">
                     <div className="compare-header-top">
                        <span className="compare-tier-name" style={{ color: col.color }}>{col.label}</span>
                        <span className="compare-tier-price" style={{ color: col.color }}>{col.price}<span className="compare-price-period">/mo</span></span>
                     </div>
                     <button
                        className={`compare-header-cta${col.action === 'current' ? ' compare-cta-current' : ''}${col.action === 'downgrade' ? ' compare-cta-downgrade' : ''}`}
                        onClick={() => col.action !== 'current' && onBecomeMember?.(col.id)}
                        disabled={checkoutLoading || col.action === 'current'}
                     >
                        {checkoutLoading
                           ? '...'
                           : col.action === 'current'
                             ? 'Current Plan'
                             : col.action === 'upgrade'
                               ? 'Upgrade'
                               : col.action === 'downgrade'
                                 ? 'Downgrade'
                                 : 'Become a Member'}
                     </button>
                     {col.action === 'current' && <span className="compare-current-badge">Current</span>}
                     {col.action !== 'current' && col.popular && <span className="compare-popular-badge">Most Popular</span>}
                  </div>
               ))}
            </div>

            {/* Divider after header */}
            <div className="compare-hdivider" />

            {/* Section: Points */}
            <div className="compare-section-label">Points</div>
            <div className="compare-row-group compare-row-bg">
               <div className="compare-row">
                  <div className="compare-label-col">Points Multiplier</div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        <span className="compare-multiplier" style={{ color: col.color }}>
                           {col.multiplier}<span className="compare-multiplier-x">×</span>
                        </span>
                     </div>
                  ))}
               </div>
            </div>

            {/* Section: Gameplay */}
            <div className="compare-section-label">Gameplay</div>
            <div className="compare-row-group compare-row-bg">
               <div className="compare-row">
                  <div className="compare-label-col">Bonus sweepstakes entries/round</div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        <div className="compare-icon-value">
                           <img src={`/assets/icons/memberships/entry-${col.slug}-icon.png`} alt="" className="compare-icon" />
                           <span>{col.entries}</span>
                        </div>
                     </div>
                  ))}
               </div>
               <div className="compare-row">
                  <div className="compare-label-col">Press Your Luck spins/day</div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        <div className="compare-icon-value">
                           <img src={`/assets/icons/memberships/spin-${col.slug}-icon.png`} alt="" className="compare-icon" />
                           <span>{col.spins}</span>
                        </div>
                     </div>
                  ))}
               </div>
            </div>

            {/* Divider */}
            <div className="compare-hdivider" />

            {/* Section: Community */}
            <div className="compare-section-label">Community</div>
            <div className="compare-row-group compare-row-bg">
               <div className="compare-row">
                  <div className="compare-label-col">Profile badge &amp; social feed ranking</div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        {col.badge ? <GreenCheck /> : <GreyDash />}
                     </div>
                  ))}
               </div>
               <div className="compare-row">
                  <div className="compare-label-col">Members-only chat</div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        {col.chat ? <GreenCheck /> : <GreyDash />}
                     </div>
                  ))}
               </div>
            </div>

            {/* Divider */}
            <div className="compare-hdivider" />

            {/* Section: Daily Grind Elite — hidden for now
            <div className="compare-section-label">Daily Grind Elite</div>
            <div className="compare-row-group compare-row-bg">
               <div className="compare-row">
                  <div className="compare-label-col compare-label-dge">
                     <img src="/assets/icons/memberships/dge-white-icon.png" alt="" className="compare-dge-icon" />
                     <span><span className="dge-gradient-text">DGE</span> included</span>
                  </div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        {col.dge === 'included' ? <GreenCheck /> : <span className="compare-optional">Optional</span>}
                     </div>
                  ))}
               </div>
            </div>
            */}

            <div className="compare-row-group compare-row-bg">
               <div className="compare-row">
                  <div className="compare-label-col">Dedicated Membership Experience Coordinator</div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        {col.coordinator ? <GreenCheck /> : <GreyDash />}
                     </div>
                  ))}
               </div>

               {/* Divider within group */}
               <div className="compare-hdivider" />

               <div className="compare-row">
                  <div className="compare-label-col">Founder-level access</div>
                  {compareColumns.map((col) => (
                     <div key={col.slug} className="compare-cell">
                        {col.founder ? <GreenCheck /> : <GreyDash />}
                     </div>
                  ))}
               </div>
            </div>
            </div>
         </div>
      </div>
   );
}
